import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputSelectField } from "../../../../components/Shared/InputSelectField";
import InputDateTime from "../../../../components/Shared/InputDateTime";
import { InputField } from "../../../../components/Shared/InputField";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    visibility: any;
    toastify: any;
    fixedLoadTypes?: string[];
    onClose?: any;
}

type TInputForm = {
    drivers: [],
    dealers: [],
    load_types: [],
    transport_vehicles: [],
    from_locations: [],
    to_locations: [],
    legal_entities: [],
    clients: [],
    providers: [],
};

const DEFAULT_FORM_STATE = {
    drivers: [],
    dealers: [],
    transport_vehicles: [],
    load_types: ['OutboundLoad', 'InboundLoad'],
    from_locations: [],
    to_locations: [],
    legal_entities: [],
    clients: [],
    providers: [],
    from_date: null,
    is_invoiced: null,
    to_date: null,
    vin_numbers: null,
    from_load_created_at: null,
    to_load_created_at: null,
    from_group_created_at: null,
    to_group_created_at: null,
};

// @ts-ignore
function LoadFilter(props: IFormProps) {
    const { t, visibility, dropdowns } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);
    const fixedLoadTypes = props.fixedLoadTypes ?? DEFAULT_FORM_STATE.load_types;
    const isOutboundTab = (
        Array.isArray(fixedLoadTypes)
        && fixedLoadTypes.length === 1
        && fixedLoadTypes[0] === 'OutboundLoad'
    );

    const formatDateOnly = (value: any): string | null => {
        if (!value) {
            return null;
        }

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            const yyyy = value.getFullYear();
            const mm = String(value.getMonth() + 1).padStart(2, '0');
            const dd = String(value.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }

        const rawValue = String(value).trim();
        if (!rawValue) {
            return null;
        }

        const directDateMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
        if (directDateMatch) {
            return directDateMatch[1];
        }

        const parsed = new Date(rawValue);
        if (!Number.isNaN(parsed.getTime())) {
            const yyyy = parsed.getFullYear();
            const mm = String(parsed.getMonth() + 1).padStart(2, '0');
            const dd = String(parsed.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }

        return null;
    };

    const onSubmit = async (payloads: any) => {
        const fromLoadCreatedAt = formatDateOnly(payloads?.from_load_created_at);
        const toLoadCreatedAt = formatDateOnly(payloads?.to_load_created_at);
        const fromGroupCreatedAt = formatDateOnly(payloads?.from_group_created_at);
        const toGroupCreatedAt = formatDateOnly(payloads?.to_group_created_at);

        props.setQuery({
            ...props.moduleState,
            filters: {
                ...payloads,
                from_load_created_at: fromLoadCreatedAt ? `${fromLoadCreatedAt} 00:00:00` : null,
                to_load_created_at: toLoadCreatedAt ? `${toLoadCreatedAt} 23:59:59` : null,
                from_group_created_at: fromGroupCreatedAt ? `${fromGroupCreatedAt} 00:00:00` : null,
                to_group_created_at: toGroupCreatedAt ? `${toGroupCreatedAt} 23:59:59` : null,
                load_types: fixedLoadTypes,
            }
        });
    };

    const onReset = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
        }

        props.setQuery({
            ...props.moduleState,
            filters: {
                load_types: fixedLoadTypes,
            }
        });
    };

    const onClose = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    useEffect(() => {
        if ( visibility ) {
            setPanelState(visibility);
            const currentFilters = props.moduleState?.filters;
            const filtersObject = (
                currentFilters
                && typeof currentFilters === 'object'
                && !Array.isArray(currentFilters)
            ) ? currentFilters : {};

            setInitFormState({
                ...DEFAULT_FORM_STATE,
                ...filtersObject,
                load_types: fixedLoadTypes,
            });
        }
    }, [visibility]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('advance filters') }</h5>
                            <p>{ t("using filters you can archive better result.") }</p>
                        </div>
                    }
                    visible={ panelState } position="left" onHide={ onClose } blockScroll={ true }
                    style={ { width: '800px' } } closeOnEscape={false}
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="brands"
                                                   options={ dropdowns?.brands }
                                                   label={ t('brands') }
                                                   placeholder={ t('select') +' '+ t('brands') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="models"
                                                   options={ dropdowns?.models }
                                                   label={ t('models') }
                                                   placeholder={ t('select') +' '+ t('models') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="drivers"
                                                   options={ dropdowns?.drivers }
                                                   label={ t('drivers') }
                                                   placeholder={ t('select') +' '+ t('drivers') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="transport_vehicles"
                                                   options={ dropdowns.transportVehicles }
                                                   label={ t('transport vehicles') }
                                                   placeholder={ t('select') +' '+ t('transport vehicles') }
                                                   getOptionLabel={ (option: any) => `${ option.truck_plate_number } | ${ option.trailer_plate_number }` }
                                                   optionLabel="trailer_plate_number"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="from_locations"
                                                   options={ dropdowns?.locations }
                                                   label={ t('from locations') }
                                                   placeholder={ t('select') +' '+ t('from locations') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="dealers"
                                                   options={ dropdowns?.dealers }
                                                   label={ t('dealers') }
                                                   placeholder={ t('select') +' '+ t('dealers') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="to_locations"
                                                   options={ dropdowns?.locations }
                                                   label={ t('to locations') }
                                                   placeholder={ t('select') +' '+ t('to locations') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="legal_entities"
                                                   options={ dropdowns?.legalEntities }
                                                   label={ t('legal entities') }
                                                   placeholder={ t('select') +' '+ t('legal entities') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="clients"
                                                   options={ dropdowns?.clients }
                                                   label={ t('clients') }
                                                   placeholder={ t('select') +' '+ t('clients') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="providers"
                                                   options={ dropdowns?.providers || [] }
                                                   label={ t('providers') }
                                                   placeholder={ t('select') +' '+ t('providers') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="from_cities"
                                                   options={ dropdowns?.cities }
                                                   label={ t('from cities') }
                                                   placeholder={ t('select') +' '+ t('from cities') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="to_cities"
                                                   options={ dropdowns?.cities }
                                                   label={ t('to cities') }
                                                   placeholder={ t('select') +' '+ t('to cities') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="from_countries"
                                                   options={ dropdowns?.countries }
                                                   label={ t('from countries') }
                                                   placeholder={ t('select') +' '+ t('from countries') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="to_countries"
                                                   options={ dropdowns?.countries }
                                                   label={ t('to countries') }
                                                   placeholder={ t('select') +' '+ t('to countries') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="load_statuses"
                                                   options={ dropdowns?.loadStatuses }
                                                   label={ t('load status') }
                                                   placeholder={ t('select') +' '+ t('load status') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                                   isMulti
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="vin_numbers"
                                                   label={ t("vin numbers") }
                                                   formText={ t("a series of vin number values, each separated by a space") }
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSelectField }
                                                   name="is_invoiced"
                                                   options={ [
                                                       { "id": "no", "label": t("no") },
                                                       { "id": "yes", "label": t("yes") },
                                                   ] }
                                                   label={ t('should be invoiced') }
                                                   optionLabel="label"
                                                   optionValue="id"
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="from_load_created_at"
                                                   type={ "date" }
                                                   label={ t("from load created at") }
                                                   isClearable={ true }
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="to_load_created_at"
                                                   type={ "date" }
                                                   label={ t("to load created at") }
                                                   isClearable={ true }
                                            />
                                        </Col>

                                        {
                                            isOutboundTab &&
                                            <>
                                                <Col sm={ 12 } md={ 6 }>
                                                    <Field component={ InputDateTime }
                                                           name="from_group_created_at"
                                                           type={ "date" }
                                                           label={ t("from group created at") }
                                                           isClearable={ true }
                                                    />
                                                </Col>

                                                <Col sm={ 12 } md={ 6 }>
                                                    <Field component={ InputDateTime }
                                                           name="to_group_created_at"
                                                           type={ "date" }
                                                           label={ t("to group created at") }
                                                           isClearable={ true }
                                                    />
                                                </Col>
                                            </>
                                        }

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="from_loading_date"
                                                   type={ "datetime" }
                                                   label={ t("from loading date") }
                                                   isClearable={ true }
                                                   enableTimeStartOfDay
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="to_loading_date"
                                                   type={ "datetime" }
                                                   label={ t("to loading date") }
                                                   isClearable={ true }
                                                   enableTimeEndOfDay
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="from_unloading_date"
                                                   type={ "datetime" }
                                                   label={ t("from unloading date") }
                                                   isClearable={ true }
                                                   enableTimeStartOfDay
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputDateTime }
                                                   name="to_unloading_date"
                                                   type={ "datetime" }
                                                   label={ t("to unloading date") }
                                                   isClearable={ true }
                                                   enableTimeEndOfDay
                                            />
                                        </Col>
                                    </Row>
                                    <div className="filter filter-card-footer"
                                         style={ { width: '785px' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("to data save into database") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-filter me-2" />
                                            { t("filter") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onReset }
                                                tooltip={ t("to make all fields empty") } className={ "ms-2" }
                                                size={ "small" } severity={ "warning" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-refresh me-2" />
                                            { t("reset") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onClose }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("close") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

LoadFilter.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(LoadFilter);
